import { useState, useEffect, useRef } from 'react'
import { X, Upload, Trash2, PenTool, AlertCircle } from 'lucide-react'
import api from '../../core/services/api'
import { Toast } from '../Toast'

interface FirmaUploadModalProps {
  isOpen: boolean
  onClose: () => void
  personaleId: string
  nomeCognome: string
  hasFirma: boolean
  onFirmaChanged: () => void
}

export function FirmaUploadModal({
  isOpen,
  onClose,
  personaleId,
  nomeCognome,
  hasFirma,
  onFirmaChanged
}: FirmaUploadModalProps) {
  const [firmaUrl, setFirmaUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && hasFirma) {
      loadFirma()
    }
    if (!isOpen) {
      setFirmaUrl(null)
      setSelectedFile(null)
      setPreviewUrl(null)
    }
  }, [isOpen, hasFirma, personaleId])

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (firmaUrl) window.URL.revokeObjectURL(firmaUrl)
      if (previewUrl) window.URL.revokeObjectURL(previewUrl)
    }
  }, [firmaUrl, previewUrl])

  const loadFirma = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/personale/${personaleId}/firma`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      setFirmaUrl(url)
    } catch {
      // Firma non presente
      setFirmaUrl(null)
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setSelectedFile(file)
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
    if (file) {
      setPreviewUrl(window.URL.createObjectURL(file))
    } else {
      setPreviewUrl(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    if (selectedFile.type !== 'image/png') {
      setToast({ message: 'Solo file PNG accettati', type: 'error' })
      return
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      setToast({ message: 'File troppo grande (max 2 MB)', type: 'error' })
      return
    }

    try {
      setUploading(true)
      const formData = new FormData()
      formData.append('file', selectedFile)

      await api.post(`/v1/personale/${personaleId}/firma`, formData)

      setToast({ message: 'Firma caricata', type: 'success' })
      setSelectedFile(null)
      setPreviewUrl(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      onFirmaChanged()
      loadFirma()
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Errore nel caricamento'
      setToast({ message: msg, type: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    try {
      await api.delete(`/v1/personale/${personaleId}/firma`)
      setToast({ message: 'Firma rimossa', type: 'success' })
      if (firmaUrl) window.URL.revokeObjectURL(firmaUrl)
      setFirmaUrl(null)
      onFirmaChanged()
    } catch {
      setToast({ message: 'Errore nella rimozione', type: 'error' })
    }
  }

  if (!isOpen) return null

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-primary-600" />
              Firma - {nomeCognome}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Firma esistente */}
            {loading ? (
              <div className="text-center py-6 text-gray-500">Caricamento...</div>
            ) : firmaUrl ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Firma attuale:</p>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex justify-center">
                  <img
                    src={firmaUrl}
                    alt="Firma"
                    className="max-h-32 object-contain"
                  />
                </div>
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Rimuovi firma
                </button>
              </div>
            ) : (
              <div className="text-center py-4 text-gray-400">
                <PenTool className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessuna firma caricata</p>
              </div>
            )}

            {/* Upload */}
            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {firmaUrl ? 'Sostituisci firma:' : 'Carica firma:'}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
              >
                Scegli file
              </button>
              <span className="ml-3 text-sm text-gray-500">
                {selectedFile ? selectedFile.name : 'Nessun file selezionato'}
              </span>

              {previewUrl && (
                <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50 flex justify-center">
                  <img
                    src={previewUrl}
                    alt="Anteprima"
                    className="max-h-24 object-contain"
                  />
                </div>
              )}

              {selectedFile && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Caricamento...' : 'Carica Firma'}
                </button>
              )}
            </div>

            {/* Info */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                Solo file PNG, max 2 MB. La firma verra' utilizzata per la generazione di documenti.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
